import type { ElementType, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The mono micro-label - the most-used element in the design.
 *
 * Mono, ~10-11px, uppercase, 0.12em tracking, dim. It carries every piece of system-authored
 * text: table headers, field labels, page subtitles, IDs, metadata, badge text, bucket paths.
 *
 * The rule of thumb: if a human wrote it, it is sans; if the system produced it, it is this.
 * Ad-hoc mono/uppercase/tracking classes anywhere else in the app are a defect.
 */
export function MicroLabel({
  children,
  as: Tag = 'span',
  tone = 'dim',
  size = 'sm',
  caps = true,
  className
}: {
  children: ReactNode;
  as?: ElementType;
  tone?: 'dim' | 'muted' | 'foreground' | 'primary' | 'pass' | 'warn' | 'danger';
  size?: 'xs' | 'sm';
  /**
   * Labels are uppercase; VALUES are not. The mockups render "LeRobot v2.1", "Jul 15" and
   * "14 datasets certified" in mono at their natural case - uppercasing them is a fidelity bug.
   */
  caps?: boolean;
  className?: string;
}) {
  return (
    <Tag
      className={cn(
        'font-mono',
        // Wide tracking is a property of the uppercase label. Lowercase values read better
        // tight, and the extra tracking is what pushed stat-tile footnotes onto two lines.
        caps ? 'uppercase tracking-[0.12em]' : 'tracking-[0.02em]',
        size === 'xs' ? 'text-[10px]' : 'text-[11px]',
        {
          dim: 'text-dim',
          muted: 'text-muted',
          foreground: 'text-foreground',
          primary: 'text-primary',
          pass: 'text-pass',
          warn: 'text-warn',
          danger: 'text-danger'
        }[tone],
        className
      )}
    >
      {children}
    </Tag>
  );
}
